import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "@/lib/auth";
import { PLATFORM } from "@/lib/platform";
import { CustomerSessionPill } from "@/components/CustomerOnboarding";
import { LayoutDashboard, LayoutGrid, Package, Plus, LogOut, UploadCloud, Users, Settings, Sparkles } from "lucide-react";

// The section tabs under the header, in the order of the old sidebar.
// `on`: the paths a tab is active for (default: its own path and below).
const NAV = [
  { to: "/admin", on: (p) => /^\/admin\/?$/.test(p), label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard-link" },
  {
    to: "/admin/products",
    on: (p) => /^\/admin\/products(\/[^/]+\/edit)?\/?$/.test(p),
    label: "Products",
    icon: Package,
    testId: "nav-products-link",
  },
  { to: "/admin/products/new", label: "New Product", icon: Plus, testId: "nav-new-product-link" },
  { to: "/admin/products/bulk", label: "Bulk Upload", icon: UploadCloud, testId: "nav-bulk-upload-link" },
  { to: "/admin/ask", label: "Ask Heda AI", icon: Sparkles, testId: "nav-ask-heda-link", gold: true },
  { to: "/admin/customers", label: "Customers", icon: Users, testId: "nav-customers-link" },
  { to: "/admin/rebrand", label: "PDF Rebrand", icon: UploadCloud, testId: "nav-rebrand-link" },
  { to: "/admin/settings", label: "Settings", icon: Settings, testId: "nav-settings-link" },
  { to: "/admin/users", label: "Team", icon: Users, testId: "nav-users-link", ownerOnly: true },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="min-h-screen bg-heda-cream text-heda-ink">
      <header
        className="sticky top-0 z-30 border-b border-heda-line bg-heda-cream/95 backdrop-blur"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 md:px-8 md:py-4">
          <div className="flex min-w-0 items-center gap-3">
            <Logo />
            <div className="min-w-0 leading-tight">
              <h1 className="font-display text-[17px] font-bold text-heda-ink sm:text-[19px]">Hedaply</h1>
              <p className="text-[10.5px] text-heda-mute [overflow-wrap:anywhere]">
                AI Product Studio · Signed in as{" "}
                <span className="font-semibold text-heda-ink/85">{user?.email}</span>
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {PLATFORM && (
              <a href="/" className="heda-pill" data-testid="nav-all-apps-link" title="All apps">
                <LayoutGrid size={12} />
                <span className="hidden sm:inline">All apps</span>
              </a>
            )}
            <button
              data-testid="admin-logout-button"
              onClick={() => {
                logout();
                if (!PLATFORM) nav("/admin/login");
              }}
              className="heda-pill-ink"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>

        <nav className="mx-auto flex max-w-7xl items-center gap-1 overflow-x-auto border-t border-heda-line/60 px-4 md:px-8">
          {NAV.filter((n) => !n.ownerOnly || user?.role === "owner").map(({ to, on, label, icon: Icon, testId, gold }) => (
            <Link
              key={to}
              to={to}
              data-testid={testId}
              className={`heda-tab ${(on ? on(pathname) : pathname.startsWith(to)) ? "is-on" : ""}`}
            >
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <Icon size={13} className={gold ? "text-heda-gold" : ""} /> {label}
              </span>
            </Link>
          ))}
        </nav>
      </header>

      <main className="mx-auto w-full min-w-0 max-w-7xl px-4 py-6 md:px-8 md:py-10">
        <div className="mb-4 flex justify-end">
          <CustomerSessionPill />
        </div>
        {children}
      </main>
    </div>
  );
}
